import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// Molarity M = n / V ;   n = m / MW
// Provide all three modes plus a formula-based molar mass helper.
export default function MolarityCalc() {
  const [mode, setMode] = useState('molarity'); // molarity | mass | volume
  const [mass, setMass] = useState(58.44);
  const [mw, setMw] = useState(58.44);
  const [volume, setVolume] = useState(1);
  const [volUnit, setVolUnit] = useState('L');
  const [molarity, setMolarity] = useState(1);
  const [massUnit, setMassUnit] = useState('g');

  const MASS_TO_G = { g:1, mg:0.001, kg:1000, ug:1e-6, ng:1e-9, oz:28.3495, lb:453.592 };
  const massG = () => MASS_TO_G[massUnit] * Number(mass);
  const volL = () => ({ L:1, mL:0.001, uL:1e-6, 'gal(US)':3.78541 })[volUnit] * Number(volume);

  const result = useMemo(() => {
    if (mode === 'molarity') {
      const n = massG() / Number(mw);
      if (!volL()) return null;
      return { label: 'Molarity (mol/L)', value: (n / volL()).toFixed(4) };
    }
    if (mode === 'mass') {
      const n = Number(molarity) * volL();
      return { label: `Mass (${massUnit})`, value: (n * Number(mw) / MASS_TO_G[massUnit]).toFixed(4) };
    }
    const n = massG() / Number(mw);
    if (!Number(molarity)) return null;
    return { label: `Volume (${volUnit})`, value: (n / Number(molarity) / ({ L:1, mL:0.001, uL:1e-6, 'gal(US)':3.78541 })[volUnit]).toFixed(4) };
  }, [mode, mass, mw, volume, volUnit, molarity, massUnit]);

  return (
    <CalcShell look="cnet" title="Molarity Calculator" subtitle="Compute molarity (mol/L), mass or volume for a solution using M = n / V.">
      <div className="flex gap-2 border-b mb-4">
        {[['molarity','Find Molarity'],['mass','Find Mass'],['volume','Find Volume']].map(([v,l]) => (
          <button key={v} className={`px-4 py-2 text-sm font-semibold ${mode===v?'border-b-2 border-blue-600 text-blue-700':'text-slate-500'}`} onClick={() => setMode(v)}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Molecular Weight (g/mol)</label><input type="number" value={mw} onChange={e => setMw(e.target.value)} data-testid="molar-mw"/></div>
          {mode !== 'mass' && (
            <div className="field"><label>Solute Mass</label>
              <div className="flex gap-2">
                <input type="number" value={mass} onChange={e => setMass(e.target.value)} className="flex-1"/>
                <select value={massUnit} onChange={e => setMassUnit(e.target.value)}><option>g</option><option>mg</option><option>kg</option><option>ug</option><option>ng</option><option>oz</option><option>lb</option></select>
              </div>
            </div>
          )}
          {mode !== 'volume' && (
            <div className="field"><label>Solution Volume</label>
              <div className="flex gap-2">
                <input type="number" value={volume} onChange={e => setVolume(e.target.value)} className="flex-1"/>
                <select value={volUnit} onChange={e => setVolUnit(e.target.value)}><option>L</option><option>mL</option><option>uL</option><option value="gal(US)">gal (US)</option></select>
              </div>
            </div>
          )}
          {mode !== 'molarity' && (
            <div className="field"><label>Molarity (mol/L)</label><input type="number" value={molarity} onChange={e => setMolarity(e.target.value)}/></div>
          )}
        </div>
        <div>
          {result && (
            <div className="p-5 rounded-xl bg-indigo-50 border border-indigo-200">
              <div className="text-sm text-slate-600">{result.label}</div>
              <div className="text-4xl font-bold text-indigo-700" data-testid="molar-result">{result.value}</div>
              {mode === 'molarity' && (() => {
                const M = parseFloat(result.value);
                return (
                  <table className="w-full text-xs mt-3 border border-indigo-100 rounded overflow-hidden">
                    <tbody>
                      <tr className="bg-white"><td className="px-2 py-1">M (mol/L)</td><td className="px-2 py-1 text-right font-mono">{M.toFixed(6)}</td></tr>
                      <tr className="bg-indigo-50/60"><td className="px-2 py-1">mM (mmol/L)</td><td className="px-2 py-1 text-right font-mono">{(M * 1000).toFixed(4)}</td></tr>
                      <tr className="bg-white"><td className="px-2 py-1">μM (μmol/L)</td><td className="px-2 py-1 text-right font-mono">{(M * 1e6).toFixed(2)}</td></tr>
                      <tr className="bg-indigo-50/60"><td className="px-2 py-1">nM (nmol/L)</td><td className="px-2 py-1 text-right font-mono">{(M * 1e9).toFixed(1)}</td></tr>
                      <tr className="bg-white"><td className="px-2 py-1">pM (pmol/L)</td><td className="px-2 py-1 text-right font-mono">{(M * 1e12).toFixed(0)}</td></tr>
                      <tr className="bg-indigo-50/60"><td className="px-2 py-1">mol/mL</td><td className="px-2 py-1 text-right font-mono">{(M / 1000).toExponential(4)}</td></tr>
                      <tr className="bg-white"><td className="px-2 py-1">mol/m³</td><td className="px-2 py-1 text-right font-mono">{(M * 1000).toFixed(3)}</td></tr>
                    </tbody>
                  </table>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </CalcShell>
  );
}
